import {
  Injectable,
  ComponentFactoryResolver,
  Injector,
  ApplicationRef,
  TemplateRef,
  ComponentRef,
  EventEmitter
} from '@angular/core';
import swal, { SweetAlertOptions } from 'sweetalert2';
import { extend } from 'underscore';
import * as $ from 'jquery';
import { DateTimePickerComponent } from '../../shared/components/date-time-picker/date-time-picker.component';
import { Subscription } from 'rxjs';

@Injectable()
export class SwalService {
  private removeUnusedSwalInputs(modalElement: HTMLElement) {
    if (!modalElement) {
      return;
    }

    // SweetAlert2 renders template inputs for modes not used in this dialog.
    // Remove hidden templates so scanners do not report label errors on them.
    const unusedInputTemplates = modalElement.querySelectorAll(
      '.swal2-input, .swal2-file, .swal2-range, .swal2-radio, .swal2-checkbox, .swal2-textarea'
    );

    unusedInputTemplates.forEach((element: Element) => {
      const htmlElement = element as HTMLElement;
      const isHidden = window.getComputedStyle(htmlElement).display === 'none';
      if (isHidden) {
        htmlElement.remove();
      }
    });
  }
  private get defaults() {
    const options = <SweetAlertOptions>{
      cancelButtonColor: '#046993',
      confirmButtonColor: '#117E68',
      text: '',
      onOpen: () => swal.hideLoading(),
      useRejections: true
    };

    if ($('body').hasClass('modal-open')) {
      options.target = '.modal-content';
    }

    return options;
  }
  private partialRef: ComponentRef<DateTimePickerComponent>;
  private dateTimeEmitter: EventEmitter<Date>;
  private resolvedDate: Date;
  private dateTimeSub: Subscription;

  constructor(
    private readonly resolver: ComponentFactoryResolver,
    private readonly injector: Injector,
    private readonly app: ApplicationRef
  ) {}

  dismiss(onComplete?: (modalElement: HTMLElement) => void) {
    swal.close();
  }

  error(options: SweetAlertOptions) {
    options = extend(
      this.defaults,
      <SweetAlertOptions>{ confirmButtonText: 'OK' },
      options,
      <SweetAlertOptions>{ type: 'error' }
    );
    return swal(options);
  }

  load(title: string, text: string = '') {
    const options = extend(this.defaults, <SweetAlertOptions>{
      title: title,
      text: text,
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
      showConfirmButton: false,
      onOpen: () => swal.showLoading()
    });
    return swal(options);
  }

  stopLoading() {
    const options = extend(this.defaults, <SweetAlertOptions>{
      onOpen: () => swal.hideLoading()
    });
    return swal(options);
  }

  radio(
    options: SweetAlertOptions,
    pairs: { [key: string]: string }
  ): Promise<any> {
    options = extend(this.defaults, options, <SweetAlertOptions>{
      inputOptions: pairs,
      inputValidator: result =>
        new Promise<any>(resolve => {
          result
            ? resolve(undefined)
            : resolve('You must select an option in order to proceed.');
        }),
      input: 'radio',
      showCancelButton: true
    });
    return swal(options);
  }

  select(options: SweetAlertOptions) {
    const existingOnOpen = options ? options.onOpen : null;
    options = extend(this.defaults, options, {
      input: 'select',
      showCancelButton: true,
      imageUrl: null,
      imageAlt: '',
      onOpen: (modalElement: HTMLElement) => {
        // Keep any caller-specific onOpen behavior first.
        if (existingOnOpen) {
          existingOnOpen(modalElement);
        }

        this.removeUnusedSwalInputs(modalElement);

        // SweetAlert may still render a hidden image container; mark as decorative.
        const imageElement = modalElement
          ? (modalElement.querySelector('.swal2-image') as HTMLImageElement)
          : null;
        if (imageElement) {
          imageElement.alt = '';
          imageElement.setAttribute('aria-hidden', 'true');
        }
      }
    });

    return swal(options);
  }

  selfDestructIn(
    options: SweetAlertOptions,
    callback: () => void,
    seconds: number = 3
  ) {
    const millisecondsToExplode = seconds * 1000;
    options = extend(this.defaults, options, <SweetAlertOptions>{
      html: `<h3 id="selfDestructTimerLabel">${seconds}</h3>`,
      timer: millisecondsToExplode + 250,
      onOpen: () => {
        const explosionOffset = 100;
        const tick = 1000;

        // * Keep the next 'var' keyword. We need var so that this variable shares scope with the lambda
        // * in the following 'setInterval' callback
        // tslint:disable-next-line:no-var-keyword
        var secondsTilExplosion = seconds;

        const explosionTimer = setInterval(() => {
          secondsTilExplosion -= 1;
          $('#selfDestructTimerLabel').html(secondsTilExplosion.toString());
        }, tick);

        setTimeout(
          () => clearInterval(explosionTimer),
          millisecondsToExplode + explosionOffset
        );
      },
      onClose: callback
    });
    return this.unclosable(options);
  }

  success(options: SweetAlertOptions) {
    options = extend(
      this.defaults,
      <SweetAlertOptions>{ confirmButtonText: 'OK' },
      options,
      <SweetAlertOptions>{ type: 'success' }
    );
    return swal(options);
  }

  textarea(
    options: SweetAlertOptions,
    requisiteness: 'optional' | 'required',
    emtpyTextErrorMessage: string
  ): Promise<any> {
    options = extend(
      this.defaults,
      <SweetAlertOptions>{
        cancelButtonText: 'Cancel',
        confirmButtonText: 'OK',
        inputPlaceholder: 'Enter your text here.',
        showCancelButton: true
      },
      options,
      <SweetAlertOptions>{
        input: 'textarea',
        inputValidator: result =>
          new Promise<any>(resolve => {
            if (
              requisiteness === 'optional' ||
              (result && result.toString().trim() !== '')
            ) {
              resolve(undefined);
            } else {
              resolve(emtpyTextErrorMessage);
            }
          })
      }
    );
    return swal(options);
  }

  unclosable(options: SweetAlertOptions) {
    options = extend(this.defaults, options, <SweetAlertOptions>{
      showConfirmButton: false,
      showCancelButton: false,
      allowEnterKey: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showCloseButton: false
    });
    return swal(options);
  }

  warn(options: SweetAlertOptions) {
    options = extend(this.defaults, options, <SweetAlertOptions>{
      type: 'warning'
    });
    return swal(options);
  }

  yesOrNo(options: SweetAlertOptions) {
    options = extend(this.defaults, options, <SweetAlertOptions>{
      allowOutsideClick: false,
      allowEscapeKey: false,
      cancelButtonText: 'No',
      confirmButtonText: 'Yes',
      showCancelButton: true,
      type: 'question'
    });

    return swal(options);
  }

  show(options: SweetAlertOptions): Promise<any> {
    options = extend(this.defaults, options);
    return swal(options);
  }

  getSwalDate(options: SweetAlertOptions): Promise<any> {
    const that = this;
    const newOptions: SweetAlertOptions = {
      ...this.defaults,
      ...options,
      onBeforeOpen: modalElement => {
        const factory = that.resolver.resolveComponentFactory(
          DateTimePickerComponent
        );
        // add class to resize datetimepicker
        modalElement.children[1].classList.add('px-5');

        that.partialRef = factory.create(
          that.injector,
          [],
          modalElement.children[1]
        );

        //  => Apply the consumer's template on the component
        const dateTimePickerInstance = that.partialRef.instance;
        dateTimePickerInstance.IsRequired = false;
        dateTimePickerInstance.showTime = false;
        that.dateTimeEmitter = dateTimePickerInstance.dateTimeChanged;
        that.dateTimeSub = that.dateTimeEmitter
          .asObservable()
          .subscribe(date => {
            that.resolvedDate = new Date(date);
          });
        // => Make the Angular app aware of that detached view so change detection works
        that.app.attachView(that.partialRef.hostView);
      },
      onClose: modalElement => {
        that.dateTimeSub.unsubscribe();
        that.app.detachView(that.partialRef.hostView);
        that.partialRef.destroy();
      },
      preConfirm: () => {
        return Promise.resolve(that.resolvedDate);
      }
    };
    return swal(newOptions);
  }
}
